// Marcas de semáforo propias (círculo ✓, triángulo ⚠, octágono ✗): la forma dice lo mismo que el color,
// así nadie depende solo del color. Copiadas de la maqueta aprobada (`mk()`).
import { Hourglass, Star } from "lucide-react";
import type { Columna } from "@/reclutamiento/tipos";
import { MARCA_COLUMNA, type Semaforo } from "./util";
import { claseEtapa, ETAPA_VISUAL, type ClaveEtapa } from "./etapas";
import type { EstadoCriterio } from "@/reclutamiento/tipos";

/**
 * Marca de una columna: el semáforo si tiene (Revisar, Contratado, Descartados) o un reloj de arena para
 * «Algún día / Tal vez» (en pausa: ni bien ni mal). null = sin marca.
 */
export function MarcaColumna({ c, s = 14 }: { c: Columna; s?: number }) {
  if (c === "algun_dia") return <Hourglass className="mk mk-pausa" size={s} strokeWidth={2.2} aria-hidden="true" />;
  const m = MARCA_COLUMNA[c];
  return m ? <Mk k={m} s={s} /> : null;
}

export function Mk({ k, s = 18, titulo }: { k: Semaforo; s?: number; titulo?: string }) {
  const a11y = titulo ? { role: "img", "aria-label": titulo } : { "aria-hidden": true as const };
  if (k === "ok")
    return (
      <svg className="mk" width={s} height={s} viewBox="0 0 20 20" {...a11y}>
        <circle cx="10" cy="10" r="9.2" fill="var(--ok)" />
        <path d="M5.8 10.4l2.8 2.8 5.6-6" fill="none" stroke="var(--glyph)" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  if (k === "yel")
    return (
      <svg className="mk" width={s} height={s} viewBox="0 0 20 20" {...a11y}>
        <path d="M10 1.4 19.2 18H.8z" fill="var(--warn)" stroke="var(--warn)" strokeWidth="1.2" strokeLinejoin="round" />
        <path d="M10 7.2v4.6" stroke="var(--warn-glyph)" strokeWidth="2.2" strokeLinecap="round" />
        <circle cx="10" cy="14.7" r="1.25" fill="var(--warn-glyph)" />
      </svg>
    );
  return (
    <svg className="mk" width={s} height={s} viewBox="0 0 20 20" {...a11y}>
      <path d="M6.2 1h7.6L19 6.2v7.6L13.8 19H6.2L1 13.8V6.2z" fill="var(--bad)" />
      <path d="M6.8 6.8l6.4 6.4M13.2 6.8l-6.4 6.4" stroke="var(--glyph)" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

/** WhatsApp (lucide no lo trae). `relleno`: globo sólido con el auricular calado, para el botón verde. */
export function IcWhatsApp({ s = 20, relleno = false }: { s?: number; relleno?: boolean }) {
  if (relleno)
    return (
      <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2.2a9.8 9.8 0 0 0-8.5 14.7L2.2 21.8l5-1.3A9.8 9.8 0 1 0 12 2.2z" fill="currentColor" />
        <path
          d="M8.6 7.3c-.3 0-.7.1-1 .5-.3.4-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.3 5.1 4.5 2.5 1 3 .8 3.6.7.5-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.4l-2-1c-.3-.1-.5-.1-.7.1l-.9 1.2c-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.5z"
          fill="var(--wa)"
        />
      </svg>
    );
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20l1.3-4A8 8 0 1 1 8.4 19z" />
      <path d="M9 9.5c0 3 2.5 5.5 5.5 5.5l1.2-1.4-2-1-1 .8a4 4 0 0 1-2.1-2.1l.8-1-1-2z" />
    </svg>
  );
}

/**
 * Marca de un requisito: ✓ círculo verde (cumple), ? círculo ámbar (por confirmar), ✗ octágono rojo (no cumple).
 * La forma y el signo dicen lo mismo que el color.
 */
export function MkEstado({ e, s = 18, titulo }: { e: EstadoCriterio; s?: number; titulo?: string }) {
  if (e === "ok") return <Mk k="ok" s={s} titulo={titulo} />;
  if (e === "no") return <Mk k="red" s={s} titulo={titulo} />;
  const a11y = titulo ? { role: "img", "aria-label": titulo } : { "aria-hidden": true as const };
  return (
    <svg className="mk" width={s} height={s} viewBox="0 0 20 20" {...a11y}>
      <circle cx="10" cy="10" r="9.2" fill="var(--warn)" />
      <path d="M7.4 7.6a2.6 2.6 0 1 1 3.8 2.3c-.8.4-1.2.9-1.2 1.7v.5" fill="none" stroke="var(--warn-glyph)" strokeWidth="2.1" strokeLinecap="round" />
      <circle cx="10" cy="15" r="1.25" fill="var(--warn-glyph)" />
    </svg>
  );
}

/** Etiqueta brillante «⭐ Nuevo» (llegó después de tu última visita). */
export function Nuevo() {
  return (
    <span className="nuevo">
      <Star size={15} fill="currentColor" strokeWidth={1.5} aria-hidden="true" />
      Nuevo
    </span>
  );
}

/** Logo genérico de la agencia (el de la maqueta). */
export function Logo({ s = 28 }: { s?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 28 28" aria-hidden="true">
      <rect x="1" y="1" width="26" height="26" rx="7" fill="var(--ink)" />
      <path d="M9 20V8h5.2a3.6 3.6 0 0 1 0 7.2H9m5.2 0L19 20" fill="none" stroke="var(--bg)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="21" cy="8.5" r="2.2" fill="var(--acc)" />
    </svg>
  );
}

/** El ícono de una etapa dentro de su círculo de color. Decorativo: la palabra siempre va al lado. */
export function IconoEtapa({ c, s = 22, className = "" }: { c: ClaveEtapa; s?: number; className?: string }) {
  const { Icono } = ETAPA_VISUAL[c];
  return (
    <span className={`eti ${claseEtapa(c)} ${className}`} style={{ width: s, height: s }} aria-hidden="true">
      <Icono size={Math.round(s * 0.6)} strokeWidth={2.6} />
    </span>
  );
}

/** Chip de etapa: color + ícono + palabra. `texto` cambia la palabra (p. ej. «Descartado: no contestó»). */
export function ChipEtapa({ c, texto, grande = false }: { c: ClaveEtapa; texto?: string; grande?: boolean }) {
  return (
    <span className={`chip-et ${claseEtapa(c)}${grande ? " grande" : ""}`}>
      <IconoEtapa c={c} s={grande ? 24 : 20} />
      {texto ?? ETAPA_VISUAL[c].texto}
    </span>
  );
}
